import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';
import { DsTabButtonComponent } from './ds-tab-button.component';

export interface DsPaginatorPageItem {
  type: 'page';
  index: number;
  key: string;
}

export interface DsPaginatorEllipsisItem {
  type: 'ellipsis';
  key: string;
}

export type DsPaginatorItem = DsPaginatorPageItem | DsPaginatorEllipsisItem;

/**
 * Page navigation control. `page` and `pageChange` are zero-based while visible labels are
 * one-based.
 *
 * The number of visible items adapts to the width of the parent element: the arrows and the
 * widest page label are measured and only as many items as fit (up to the Figma maximum) are
 * shown.
 */
@Component({
  selector: 'ds-paginator',
  standalone: true,
  imports: [DsIconButtonV2Component, DsIconComponent, DsTabButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (normalizedPageCount > 0) {
      <nav class="ds-paginator" [attr.aria-label]="ariaLabel">
        <ds-icon-button-v2
          emphasis="terciary"
          size="xs"
          icon="KeyboardArrowLeft"
          ariaLabel="Página anterior"
          #previousArrow
          [disabled]="safePage === 0"
          (clicked)="selectPage(safePage - 1)"
        />

        <span class="ds-paginator__probe" aria-hidden="true" inert #itemProbe>
          <ds-tab-button size="md" [label]="normalizedPageCount.toString()" [selected]="true" />
        </span>

        @for (item of items; track item.key) {
          @if (item.type === 'page') {
            <ds-tab-button
              size="md"
              [label]="(item.index + 1).toString()"
              [selected]="item.index === safePage"
              [ariaLabel]="'Página ' + (item.index + 1) + ' de ' + normalizedPageCount"
              (clicked)="selectPage(item.index)"
            />
          } @else {
            <span class="ds-paginator__ellipsis" aria-hidden="true">
              <ds-icon name="MoreHoriz" size="var(--ds-paginator-ellipsis-icon-size)" />
            </span>
          }
        }

        <ds-icon-button-v2
          emphasis="terciary"
          size="xs"
          icon="KeyboardArrowRight"
          ariaLabel="Página siguiente"
          [disabled]="safePage >= normalizedPageCount - 1"
          (clicked)="selectPage(safePage + 1)"
        />
      </nav>
    }
  `,
  styles: [`
    :host {
      display: inline-flex;
      max-width: 100%;
    }

    .ds-paginator {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--ds-paginator-gap);
    }

    .ds-paginator__probe {
      position: absolute;
      visibility: hidden;
      pointer-events: none;
    }

    .ds-paginator__ellipsis {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--ds-paginator-ellipsis-color);
      line-height: 0;
    }
  `],
})
export class DsPaginatorComponent implements AfterViewInit, OnDestroy {
  @Input() pageCount = 0;
  @Input() page = 0;
  @Input() ariaLabel = 'Paginación';

  @Output() readonly pageChange = new EventEmitter<number>();

  @ViewChild('previousArrow', { read: ElementRef }) private previousArrowRef?: ElementRef<HTMLElement>;
  @ViewChild('itemProbe') private set itemProbe(ref: ElementRef<HTMLElement> | undefined) {
    this.itemProbeRef = ref;
    this.observeLayout();
  }

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private itemProbeRef?: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;
  private maxItems = DS_PAGINATOR_MAX_ITEMS;

  get normalizedPageCount(): number {
    return normalizePageCount(this.pageCount);
  }

  get safePage(): number {
    return normalizePage(this.page, this.normalizedPageCount);
  }

  get items(): readonly DsPaginatorItem[] {
    return resolveDsPaginatorItems(this.normalizedPageCount, this.safePage, this.maxItems);
  }

  ngAfterViewInit(): void {
    this.observeLayout();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  selectPage(nextPage: number): void {
    const safeNextPage = normalizePage(nextPage, this.normalizedPageCount);
    if (safeNextPage === this.safePage) return;
    this.pageChange.emit(safeNextPage);
  }

  /** Re-measures when the parent resizes or the probe changes (new page count, font load). */
  private observeLayout(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver ??= new ResizeObserver(() => this.updateMaxItems());
    this.resizeObserver.disconnect();
    const parent = this.host.nativeElement.parentElement;
    if (parent) this.resizeObserver.observe(parent);
    if (this.itemProbeRef) this.resizeObserver.observe(this.itemProbeRef.nativeElement);
  }

  private updateMaxItems(): void {
    const parent = this.host.nativeElement.parentElement;
    // The icon button host uses `display: contents`; measure its rendered button.
    const arrow = this.previousArrowRef?.nativeElement.querySelector('button');
    const probe = this.itemProbeRef?.nativeElement;
    const nav = probe?.parentElement;
    if (!parent || !arrow || !probe || !nav) return;

    const parentStyle = getComputedStyle(parent);
    const availableWidth = parent.clientWidth
      - parseFloat(parentStyle.paddingLeft)
      - parseFloat(parentStyle.paddingRight);
    const nextMaxItems = resolveDsPaginatorMaxItems(
      availableWidth,
      arrow.getBoundingClientRect().width,
      probe.getBoundingClientRect().width,
      parseFloat(getComputedStyle(nav).columnGap) || 0,
    );
    if (nextMaxItems === this.maxItems) return;
    this.zone.run(() => {
      this.maxItems = nextMaxItems;
      this.cdr.markForCheck();
    });
  }
}

/**
 * Number of items (pages + ellipses) that fit between both arrows. Every item is assumed to be
 * as wide as the widest page label, which keeps the estimate conservative for ellipses.
 */
export function resolveDsPaginatorMaxItems(
  availableWidth: number,
  arrowWidth: number,
  itemWidth: number,
  gap: number,
): number {
  const itemsWidth = availableWidth - 2 * arrowWidth - gap;
  const fitting = itemWidth + gap > 0
    ? Math.floor(itemsWidth / (itemWidth + gap))
    : DS_PAGINATOR_MAX_ITEMS;
  return clamp(fitting, 1, DS_PAGINATOR_MAX_ITEMS);
}

export function resolveDsPaginatorItems(
  pageCount: number,
  currentPage: number,
  maxItems: number = DS_PAGINATOR_MAX_ITEMS,
): readonly DsPaginatorItem[] {
  const normalizedCount = normalizePageCount(pageCount);
  if (normalizedCount === 0) return [];
  const slots = clamp(Number.isFinite(maxItems) ? Math.trunc(maxItems) : DS_PAGINATOR_MAX_ITEMS, 1, DS_PAGINATOR_MAX_ITEMS);
  if (normalizedCount <= slots) {
    return pageRange(0, normalizedCount).map(pageItem);
  }

  const safePage = normalizePage(currentPage, normalizedCount);
  if (slots < MIN_PREVIEW_ITEMS) {
    const start = clamp(safePage - Math.floor((slots - 1) / 2), 0, normalizedCount - slots);
    return pageRange(start, start + slots).map(pageItem);
  }

  const edgePageCount = slots - 2;
  const middlePageCount = Math.max(slots - 5, 1);
  const lastPage = normalizedCount - 1;

  if (safePage < edgePageCount) {
    return [...pageRange(0, edgePageCount).map(pageItem), ellipsisItem('end'), pageItem(lastPage)];
  }
  if (safePage >= normalizedCount - edgePageCount) {
    return [
      pageItem(0),
      ellipsisItem('start'),
      ...pageRange(normalizedCount - edgePageCount, normalizedCount).map(pageItem),
    ];
  }
  return [
    pageItem(0),
    ellipsisItem('start'),
    ...pageRange(safePage, safePage + middlePageCount).map(pageItem),
    ellipsisItem('end'),
    pageItem(lastPage),
  ];
}

function normalizePageCount(pageCount: number): number {
  return Number.isFinite(pageCount) ? Math.max(Math.trunc(pageCount), 0) : 0;
}

function normalizePage(page: number, pageCount: number): number {
  const normalizedPage = Number.isFinite(page) ? Math.trunc(page) : 0;
  return Math.min(Math.max(normalizedPage, 0), Math.max(pageCount - 1, 0));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function pageRange(start: number, endExclusive: number): number[] {
  return Array.from({ length: Math.max(endExclusive - start, 0) }, (_, index) => start + index);
}

function pageItem(index: number): DsPaginatorPageItem {
  return { type: 'page', index, key: `page-${index}` };
}

function ellipsisItem(position: 'start' | 'end'): DsPaginatorEllipsisItem {
  return { type: 'ellipsis', key: `ellipsis-${position}` };
}

/** Maximum items between the arrows, as defined in Figma. */
export const DS_PAGINATOR_MAX_ITEMS = 10;

/** Below this many items there is no room for `1 … n … last`; a plain page window is shown. */
const MIN_PREVIEW_ITEMS = 5;
